// Cross-device state sync.
//
// Each shell fetches state once on mount via loadState(). Without any
// refresh mechanism, a user who edits a member on the web won't see that
// update on their mobile session (or vice-versa) until they hit F5. This
// hook fills the gap with three triggers:
//
//   1. Window focus         — user tabbed back to this shell after doing
//                             something on the other device.
//   2. Tab visibility       — same idea for mobile browsers where switching
//                             apps fires visibilitychange, not focus.
//   3. Periodic polling     — while the tab is visible, refetch every
//                             POLL_MS so passive users see updates too.
//
// A minimum interval between reloads (COOLDOWN_MS) prevents thundering
// herds when focus + visibilitychange fire at the same moment, or when
// the poll timer coincides with a manual mutation reload.
//
// Pass in the shell's `reload` fn. That's the same one views call after
// a local mutation, so the state pipeline is identical.

import { useEffect, useRef } from "react";

const POLL_MS      = 45_000;   // every 45s while visible
const COOLDOWN_MS  = 5_000;    // no more than one refetch per 5s

export function useAutoReload(reload) {
  const lastRunRef = useRef(0);
  const busyRef    = useRef(false);

  useEffect(() => {
    if (typeof reload !== "function") return;

    async function safeReload(source) {
      const now = Date.now();
      if (busyRef.current) return;
      if (now - lastRunRef.current < COOLDOWN_MS) return;
      lastRunRef.current = now;
      busyRef.current = true;
      try {
        await reload();
      } catch {
        // reload already surfaces errors via toast; swallow here so a
        // transient failure doesn't spam the console.
      } finally {
        busyRef.current = false;
      }
    }

    function onFocus()   { safeReload("focus"); }
    function onVisible() { if (document.visibilityState === "visible") safeReload("visibility"); }
    function onTick()    { if (document.visibilityState === "visible") safeReload("poll"); }

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(onTick, POLL_MS);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [reload]);
}
